import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ScaleIcon, UsersIcon, TrendingDownIcon, DatabaseIcon, ShieldAlertIcon, ServerCrashIcon, AlertTriangleIcon } from 'lucide-react';
export function AssessmentResultsPage() {
  const location = useLocation();
  const answers = location.state?.answers || {};
  return <div className="w-full min-h-screen bg-[#f8f9fa] flex flex-col font-sans" data-id="element-556">
      {/* Header */}
      <header className="w-full bg-[#1a0647] px-6 py-4" data-id="element-557">
        <div className="max-w-6xl mx-auto flex items-center" data-id="element-558">
          <Link to="/" className="flex-shrink-0" data-id="element-559">
            <img src="https://cdn.magicpatterns.com/uploads/ddFjEWvaDfXw4mBqdFC5Gd/Caveo_Logotype_Horizontal_240x50_White_PNG.png" alt="Caveo" className="h-5" data-id="element-560" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12 space-y-12" data-id="element-561">
        {/* Top Section: Score & Alerts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-562">
          {/* Score Gauge Card */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex flex-col items-center justify-center" data-id="element-563">
            <div className="relative w-64 h-40 flex flex-col items-center justify-end overflow-hidden mb-4" data-id="element-564">
              {/* SVG Gauge */}
              <svg viewBox="0 0 200 110" className="absolute top-0 left-0 w-full h-full" data-id="element-565">
                {/* Background Arc */}
                <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#f0f0f0" strokeWidth="16" strokeLinecap="round" data-id="element-566" />
                {/* Foreground Arc (Green) - approx 80% filled */}
                <path d="M 20 100 A 80 80 0 0 1 150 40" fill="none" stroke="#98d077" strokeWidth="16" strokeLinecap="round" data-id="element-567" />
                {/* Indicator Circle */}
                <circle cx="150" cy="40" r="6" fill="white" stroke="#98d077" strokeWidth="4" data-id="element-568" />
                {/* Segment Gaps (White lines to break the arc) */}
                <path d="M 60 20 L 70 35" stroke="white" strokeWidth="4" fill="none" transform="rotate(-30 100 100)" data-id="element-569" />
                <path d="M 130 20 L 140 35" stroke="white" strokeWidth="4" fill="none" transform="rotate(30 100 100)" data-id="element-570" />
              </svg>

              {/* Score Text */}
              <div className="text-center z-10 mt-12" data-id="element-571">
                <div className="text-6xl font-bold text-[#2d2a54] leading-none mb-1" data-id="element-572">
                  684
                </div>
                <div className="text-3xl font-bold text-[#98d077]" data-id="element-573">B</div>
              </div>
            </div>

            <p className="text-[#6b7280] text-sm font-medium text-center" data-id="element-574">
              The industry average score for a secure
              <br data-id="element-575" />
              family is <span className="text-[#98d077]" data-id="element-576">685</span>
            </p>
          </div>

          {/* Security Alerts Card */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex flex-col items-center justify-center" data-id="element-577">
            <div className="flex items-center justify-center gap-3 mb-2" data-id="element-578">
              <span className="text-6xl font-bold text-[#2d2a54]" data-id="element-579">21</span>
              <div className="bg-red-50 p-2 rounded-full" data-id="element-580">
                <AlertTriangleIcon className="w-8 h-8 text-[#f28b82]" fill="#f28b82" stroke="white" data-id="element-581" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-[#7a809b] mb-8" data-id="element-582">
              Security Alerts Detected
            </h2>

            <div className="grid grid-cols-3 gap-4 w-full" data-id="element-583">
              {/* High Risk */}
              <div className="bg-[#fafafa] rounded-2xl p-4 flex flex-col items-center justify-center border border-gray-100" data-id="element-584">
                <span className="text-4xl font-bold text-[#f28b82] mb-1" data-id="element-585">
                  4
                </span>
                <span className="text-sm font-bold text-[#7a809b]" data-id="element-586">
                  High Risk
                </span>
              </div>
              {/* Medium Risk */}
              <div className="bg-[#fafafa] rounded-2xl p-4 flex flex-col items-center justify-center border border-gray-100" data-id="element-587">
                <span className="text-4xl font-bold text-[#fbbc04] mb-1" data-id="element-588">
                  0
                </span>
                <span className="text-sm font-bold text-[#7a809b]" data-id="element-589">
                  Medium Risk
                </span>
              </div>
              {/* Low Risk */}
              <div className="bg-[#fafafa] rounded-2xl p-4 flex flex-col items-center justify-center border border-gray-100" data-id="element-590">
                <span className="text-4xl font-bold text-[#62b073] mb-1" data-id="element-591">
                  17
                </span>
                <span className="text-sm font-bold text-[#7a809b]" data-id="element-592">
                  Low Risk
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section: Attack Likelihood */}
        <div data-id="element-593">
          <h2 className="text-3xl font-bold text-[#2d2a54] text-center mb-8" data-id="element-594">
            Attack Likelihood
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-595">
            {/* Legal Consequences */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex flex-col items-center text-center" data-id="element-596">
              <h3 className="text-lg font-bold text-[#2d2a54] mb-6" data-id="element-597">
                Legal Consequences
              </h3>
              <div className="w-32 h-32 bg-blue-50 rounded-full flex items-center justify-center mb-8" data-id="element-598">
                <ScaleIcon className="w-16 h-16 text-blue-400" data-id="element-599" />
              </div>
              {/* Progress Bar - High (Red) */}
              <div className="w-full flex items-center gap-1 mb-3 relative" data-id="element-600">
                <div className="h-2.5 flex-1 bg-[#f28b82] rounded-l-full" data-id="element-601"></div>
                <div className="h-2.5 flex-1 bg-[#f28b82]" data-id="element-602"></div>
                <div className="h-2.5 flex-1 bg-[#f28b82] rounded-r-full relative" data-id="element-603">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-4 border-[#f28b82] rounded-full translate-x-1/2" data-id="element-604"></div>
                </div>
              </div>
              <span className="text-sm font-bold text-[#f28b82]" data-id="element-605">
                High Likelihood
              </span>
            </div>

            {/* Reputation Damage */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex flex-col items-center text-center" data-id="element-606">
              <h3 className="text-lg font-bold text-[#2d2a54] mb-6" data-id="element-607">
                Reputation Damage
              </h3>
              <div className="w-32 h-32 bg-indigo-50 rounded-full flex items-center justify-center mb-8" data-id="element-608">
                <UsersIcon className="w-16 h-16 text-indigo-400" data-id="element-609" />
              </div>
              {/* Progress Bar - High (Red) */}
              <div className="w-full flex items-center gap-1 mb-3 relative" data-id="element-610">
                <div className="h-2.5 flex-1 bg-[#f28b82] rounded-l-full" data-id="element-611"></div>
                <div className="h-2.5 flex-1 bg-[#f28b82]" data-id="element-612"></div>
                <div className="h-2.5 flex-1 bg-[#f28b82] rounded-r-full relative" data-id="element-613">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-4 border-[#f28b82] rounded-full translate-x-1/2" data-id="element-614"></div>
                </div>
              </div>
              <span className="text-sm font-bold text-[#f28b82]" data-id="element-615">
                High Likelihood
              </span>
            </div>

            {/* Financial Loss */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex flex-col items-center text-center" data-id="element-616">
              <h3 className="text-lg font-bold text-[#2d2a54] mb-6" data-id="element-617">
                Financial Loss
              </h3>
              <div className="w-32 h-32 bg-emerald-50 rounded-full flex items-center justify-center mb-8" data-id="element-618">
                <TrendingDownIcon className="w-16 h-16 text-emerald-400" data-id="element-619" />
              </div>
              {/* Progress Bar - High (Red) */}
              <div className="w-full flex items-center gap-1 mb-3 relative" data-id="element-620">
                <div className="h-2.5 flex-1 bg-[#f28b82] rounded-l-full" data-id="element-621"></div>
                <div className="h-2.5 flex-1 bg-[#f28b82]" data-id="element-622"></div>
                <div className="h-2.5 flex-1 bg-[#f28b82] rounded-r-full relative" data-id="element-623">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-4 border-[#f28b82] rounded-full translate-x-1/2" data-id="element-624"></div>
                </div>
              </div>
              <span className="text-sm font-bold text-[#f28b82]" data-id="element-625">
                High Likelihood
              </span>
            </div>

            {/* Data Breaches */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex flex-col items-center text-center" data-id="element-626">
              <h3 className="text-lg font-bold text-[#2d2a54] mb-6" data-id="element-627">
                Data Breaches
              </h3>
              <div className="w-32 h-32 bg-cyan-50 rounded-full flex items-center justify-center mb-8" data-id="element-628">
                <DatabaseIcon className="w-16 h-16 text-cyan-400" data-id="element-629" />
              </div>
              {/* Progress Bar - Low (Green) */}
              <div className="w-full flex items-center gap-1 mb-3 relative" data-id="element-630">
                <div className="h-2.5 flex-1 bg-[#62b073] rounded-l-full relative" data-id="element-631">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-4 border-[#62b073] rounded-full translate-x-1/2 z-10" data-id="element-632"></div>
                </div>
                <div className="h-2.5 flex-1 bg-gray-200" data-id="element-633"></div>
                <div className="h-2.5 flex-1 bg-gray-200 rounded-r-full" data-id="element-634"></div>
              </div>
              <span className="text-sm font-bold text-[#62b073]" data-id="element-635">
                Low Likelihood
              </span>
            </div>

            {/* Intellectual Property Theft */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex flex-col items-center text-center" data-id="element-636">
              <h3 className="text-lg font-bold text-[#2d2a54] mb-6" data-id="element-637">
                Intellectual Property Theft
              </h3>
              <div className="w-32 h-32 bg-purple-50 rounded-full flex items-center justify-center mb-8" data-id="element-638">
                <ShieldAlertIcon className="w-16 h-16 text-purple-400" data-id="element-639" />
              </div>
              {/* Progress Bar - Low (Green) */}
              <div className="w-full flex items-center gap-1 mb-3 relative" data-id="element-640">
                <div className="h-2.5 flex-1 bg-[#62b073] rounded-l-full relative" data-id="element-641">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-4 border-[#62b073] rounded-full translate-x-1/2 z-10" data-id="element-642"></div>
                </div>
                <div className="h-2.5 flex-1 bg-gray-200" data-id="element-643"></div>
                <div className="h-2.5 flex-1 bg-gray-200 rounded-r-full" data-id="element-644"></div>
              </div>
              <span className="text-sm font-bold text-[#62b073]" data-id="element-645">
                Low Likelihood
              </span>
            </div>

            {/* Operational Disruptions */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 flex flex-col items-center text-center" data-id="element-646">
              <h3 className="text-lg font-bold text-[#2d2a54] mb-6" data-id="element-647">
                Operational Disruptions
              </h3>
              <div className="w-32 h-32 bg-orange-50 rounded-full flex items-center justify-center mb-8" data-id="element-648">
                <ServerCrashIcon className="w-16 h-16 text-orange-400" data-id="element-649" />
              </div>
              {/* Progress Bar - Low (Green) */}
              <div className="w-full flex items-center gap-1 mb-3 relative" data-id="element-650">
                <div className="h-2.5 flex-1 bg-[#62b073] rounded-l-full relative" data-id="element-651">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-4 border-[#62b073] rounded-full translate-x-1/2 z-10" data-id="element-652"></div>
                </div>
                <div className="h-2.5 flex-1 bg-gray-200" data-id="element-653"></div>
                <div className="h-2.5 flex-1 bg-gray-200 rounded-r-full" data-id="element-654"></div>
              </div>
              <span className="text-sm font-bold text-[#62b073]" data-id="element-655">
                Low Likelihood
              </span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex justify-center pt-8 pb-12" data-id="element-656">
          <Link to="/pricing" className="bg-[#631bb7] text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-[#521099] transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-657">
            Get Your Personalized Plan
          </Link>
        </div>
      </main>
    </div>;
}